import "./section2.scss";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { GIVE_BASE_CODE, GIVE_PLUS_CODE } from "src/config";
import { lowerLanguage } from "src/i18n";
import { Paths } from "src/navigation";
import { atomDossierConfig, selectorDocuments } from "src/storage";
import { CardInfoInclusionItem, CardInfoItem } from "src/types";

export const useSection2 = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedPackage, setSelectedPackage] = useState("base");

  const dossierConfig = useRecoilValue(atomDossierConfig);
  const documents = useRecoilValue(selectorDocuments);

  const PACKAGE_BASE_PRICE = `
    ${
      dossierConfig?.configurazioni.prezzi.find(
        (prezzo) => prezzo.code === GIVE_BASE_CODE
      )?.description || " -"
    }  €`;
  const PACKAGE_PLUS_PRICE = `
    ${
      dossierConfig?.configurazioni.prezzi.find(
        (prezzo) => prezzo.code === GIVE_PLUS_CODE
      )?.description || " -"
    }  €`;

  const goToDataEntry = (base: boolean) => {
    navigate({
      pathname: Paths.DataEntry1,
      search: `?${new URLSearchParams({
        landing: "true",
        base: `${base}`,
      }).toString()}`,
    });
  };

  /**
   * Returns a list of coverage items with inclusion flags
   * based on the selected insurance package type.
   *
   * @param type - The package type: 'base' or 'plus'
   * @returns An array of objects indicating if each coverage is included
   */
  function getCardInfoInclusion(type: string): CardInfoInclusionItem[] {
    return [
      {
        id: "civil_liability",
        isIncluded: true,
      },
      {
        id: "death_by_accident",
        isIncluded: true,
      },
      {
        id: "hospitalisation_allowance_illness",
        isIncluded: true,
      },
      {
        id: "reimbursement_medical_expenses_accident",
        isIncluded: type === "base" ? false : true,
      },
      {
        id: "legal_protection",
        isIncluded: type === "base" ? false : true,
      },
    ];
  }

  /**
   * Static metadata for each coverage item.
   * Contains text keys used for localization (titles and bullet points).
   */
  const CARD_INFO: CardInfoItem[] = [
    {
      id: "civil_liability",
      title: "landing.section_2_civil_liability_title",
      infoList: ["landing.section_2_civil_liability_info_1"],
    },
    {
      id: "death_by_accident",
      title: "landing.section_2_accident_title",
      infoList: [
        "landing.section_2_accident_info_1",
        "landing.section_2_accident_info_2",
        "landing.section_2_accident_info_3",
      ],
    },
    {
      id: "hospitalisation_allowance_illness",
      title: "landing.section_2_hospitalisation_allowance_illness_title",
      infoList: ["landing.section_2_hospitalisation_allowance_illness_info_1"],
    },
    {
      id: "reimbursement_medical_expenses_accident",
      title: "landing.section_2_reimbursement_medical_expenses_accident_title",
      infoList: [
        "landing.section_2_reimbursement_medical_expenses_accident_info_1",
      ],
    },
    {
      id: "legal_protection",
      title: "landing.section_2_legal_protection_title",
      infoList: ["landing.section_2_legal_protection_info_1"],
    },
  ];

  /**
   * Coverage inclusion configuration for the 'base' package.
   */
  const BASE_INCLUSIONS = getCardInfoInclusion("base");

  /**
   * Coverage inclusion configuration for the 'plus' package.
   */
  const PLUS_INCLUSIONS = getCardInfoInclusion("plus");

  const openInformativeSet = () => {
    const link = documents?.find((document) => document.docCode === "1")
      ?.filePath[lowerLanguage];

    if (link) {
      window.open(link?.replaceAll('"', "").toString(), "_blank");
    }
  };

  return {
    PACKAGE_BASE_PRICE,
    PACKAGE_PLUS_PRICE,
    selectedPackage,
    BASE_INCLUSIONS,
    PLUS_INCLUSIONS,
    CARD_INFO,
    t,
    setSelectedPackage,
    goToDataEntry,
    openInformativeSet,
  };
};
